import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Card,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    Button,
    Stack,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PublicIcon from "@mui/icons-material/Public";
import MapIcon from "@mui/icons-material/Map";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

import { BOTON_PRIMARIO_SX, BOTON_SECUNDARIO_SX } from "../../../../components/ui/buttonStyles";
import RangosService from "../../api/rangos.api";
import { mapTransportistasAOpciones } from "../../utils/transportistas";
import { esCampoRequerido, esPrecioValido } from "../../utils/validators";

const CAMPOS = ["idAgencia", "isla", "codigosPostales", "precioBulto", "precioPalet"];

// Formulario de alta/edición de un rango de precios. Tiene su propio Card
// (sin CardContent), la validación y la llamada a RangosService.create/update:
// mismo reparto que PeticionForm (forms/PeticionForm.js:145-171), porque
// RangoFormPage (TASK-009) es una página, no un Dialog, y solo orquesta
// navegación y toasts reaccionando a onSave/onError/onCancel.
const RangoForm = ({ rango, transportistas, onSave, onError, onCancel }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

    const esEdicion = Boolean(rango?.id);

    const [idAgencia, setIdAgencia] = useState("");
    const [isla, setIsla] = useState("");
    const [codigosPostales, setCodigosPostales] = useState("");
    const [precioBulto, setPrecioBulto] = useState("");
    const [precioPalet, setPrecioPalet] = useState("");
    const [tocado, setTocado] = useState({});
    const [guardando, setGuardando] = useState(false);

    // Precarga los campos cuando se edita un rango existente (prop `rango`,
    // ya no `rowModal` del modal legacy); en alta llega como null y limpia
    useEffect(() => {
        setIdAgencia(rango?.idAgencia ?? "");
        setIsla(rango?.isla ?? "");
        setCodigosPostales(rango?.rango ?? "");
        setPrecioBulto(rango?.precio ?? "");
        setPrecioPalet(rango?.palet ?? "");
        setTocado({});
    }, [rango]);

    const opcionesTransportista = useMemo(
        () => mapTransportistasAOpciones(transportistas),
        [transportistas]
    );

    // Validación por campo: las funciones puras viven en utils/validators.js,
    // aquí solo se construye el mensaje específico de cada uno
    const errores = useMemo(
        () => ({
            idAgencia: esCampoRequerido(idAgencia) ? "" : "Selecciona un transportista",
            isla: esCampoRequerido(isla) ? "" : "La isla es obligatoria",
            codigosPostales: esCampoRequerido(codigosPostales)
                ? ""
                : "Indica al menos un código postal",
            precioBulto: esPrecioValido(precioBulto)
                ? ""
                : "El precio del bulto debe ser un número mayor o igual a 0",
            precioPalet: esPrecioValido(precioPalet)
                ? ""
                : "El precio del palet debe ser un número mayor o igual a 0",
        }),
        [idAgencia, isla, codigosPostales, precioBulto, precioPalet]
    );

    const formularioValido = useMemo(() => Object.values(errores).every((e) => !e), [errores]);

    const marcarTocado = (campo) => setTocado((prev) => ({ ...prev, [campo]: true }));

    const mostrarError = (campo) => Boolean(tocado[campo] && errores[campo]);

    const handleCambio = (campo, setter) => (event) => {
        setter(event.target.value);
        marcarTocado(campo);
    };

    const handleGuardar = async () => {
        if (!formularioValido) {
            // El botón ya queda deshabilitado si el formulario no es válido; esto
            // es una defensa adicional que revela cualquier error que quedara
            // oculto por no haber interactuado todavía con ese campo
            setTocado(CAMPOS.reduce((acc, campo) => ({ ...acc, [campo]: true }), {}));
            return;
        }

        setGuardando(true);
        try {
            const item = {
                idAgencia,
                isla: isla.trim(),
                rango: codigosPostales.trim(),
                precio: precioBulto,
                palet: precioPalet,
            };

            const rangoGuardado = esEdicion
                ? await RangosService.update(rango.id, item)
                : await RangosService.create(item);

            if (onSave) onSave(rangoGuardado);
        } catch (error) {
            console.error("Error al guardar rango de precios:", error);
            if (onError) onError(error.message);
        } finally {
            setGuardando(false);
        }
    };

    const handleCancelar = () => {
        if (onCancel) onCancel();
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                <Box sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant={isMobile ? "h6" : "h5"}
                            sx={{ fontWeight: 700, color: "#212529", mb: 0.5 }}
                        >
                            {esEdicion ? "Editar rango de precios" : "Nuevo rango de precios"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6c757d" }}>
                            Define el precio por transportista según isla y código postal
                        </Typography>
                    </Box>

                    <Stack spacing={3}>
                        {/* Transportista */}
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "#495057",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <LocalShippingIcon sx={{ fontSize: 18 }} />
                                Transportista *
                            </Typography>
                            <FormControl fullWidth error={mostrarError("idAgencia")}>
                                <Select
                                    id="transportista"
                                    displayEmpty
                                    value={idAgencia}
                                    onChange={handleCambio("idAgencia", setIdAgencia)}
                                    onBlur={() => marcarTocado("idAgencia")}
                                    sx={{ backgroundColor: "#fff" }}
                                >
                                    <MenuItem value="" disabled>
                                        Selecciona un transportista
                                    </MenuItem>
                                    {opcionesTransportista.map((opcion) => (
                                        <MenuItem key={opcion.value} value={opcion.value}>
                                            {opcion.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {mostrarError("idAgencia") && (
                                    <FormHelperText>{errores.idAgencia}</FormHelperText>
                                )}
                            </FormControl>
                        </Box>

                        {/* Isla */}
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "#495057",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <PublicIcon sx={{ fontSize: 18 }} />
                                Isla *
                            </Typography>
                            <TextField
                                id="isla"
                                fullWidth
                                variant="outlined"
                                placeholder="Ej: Tenerife"
                                value={isla}
                                onChange={handleCambio("isla", setIsla)}
                                onBlur={() => marcarTocado("isla")}
                                error={mostrarError("isla")}
                                helperText={mostrarError("isla") ? errores.isla : ""}
                                sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                            />
                        </Box>

                        {/* Rango de códigos postales */}
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "#495057",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <MapIcon sx={{ fontSize: 18 }} />
                                Rango de códigos postales *
                            </Typography>
                            <TextField
                                id="codigos-postales"
                                fullWidth
                                multiline
                                rows={3}
                                variant="outlined"
                                placeholder="Ej: 38001-38010, 38100"
                                value={codigosPostales}
                                onChange={handleCambio("codigosPostales", setCodigosPostales)}
                                onBlur={() => marcarTocado("codigosPostales")}
                                error={mostrarError("codigosPostales")}
                                helperText={
                                    mostrarError("codigosPostales")
                                        ? errores.codigosPostales
                                        : "Separados por comas"
                                }
                                sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                            />
                        </Box>

                        {/* Precios */}
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        mb: 1,
                                        color: "#495057",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                    }}
                                >
                                    <AttachMoneyIcon sx={{ fontSize: 18 }} />
                                    Precio del bulto *
                                </Typography>
                                <TextField
                                    id="precio-bulto"
                                    fullWidth
                                    variant="outlined"
                                    // DECISION: inputMode va en inputProps; MUI no reenvia props arbitrarias al <input>,
                                    // asi que "inputMode" suelto no llegaba al DOM y en movil no salia el teclado numerico
                                    inputProps={{ inputMode: "decimal" }}
                                    placeholder="0.00"
                                    value={precioBulto}
                                    onChange={handleCambio("precioBulto", setPrecioBulto)}
                                    onBlur={() => marcarTocado("precioBulto")}
                                    error={mostrarError("precioBulto")}
                                    helperText={mostrarError("precioBulto") ? errores.precioBulto : ""}
                                    sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        mb: 1,
                                        color: "#495057",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                    }}
                                >
                                    <AttachMoneyIcon sx={{ fontSize: 18 }} />
                                    Precio del palet *
                                </Typography>
                                <TextField
                                    id="precio-palet"
                                    fullWidth
                                    variant="outlined"
                                    // DECISION: inputMode va en inputProps; MUI no reenvia props arbitrarias al <input>,
                                    // asi que "inputMode" suelto no llegaba al DOM y en movil no salia el teclado numerico
                                    inputProps={{ inputMode: "decimal" }}
                                    placeholder="0.00"
                                    value={precioPalet}
                                    onChange={handleCambio("precioPalet", setPrecioPalet)}
                                    onBlur={() => marcarTocado("precioPalet")}
                                    error={mostrarError("precioPalet")}
                                    helperText={mostrarError("precioPalet") ? errores.precioPalet : ""}
                                    sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                                />
                            </Box>
                        </Stack>

                        {/* Botones de acción */}
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 2 }}>
                            <Button
                                variant="contained"
                                startIcon={
                                    guardando ? (
                                        <CircularProgress size={18} sx={{ color: "#fff" }} />
                                    ) : (
                                        <SaveIcon />
                                    )
                                }
                                onClick={handleGuardar}
                                disabled={guardando || !formularioValido}
                                fullWidth={isMobile}
                                sx={BOTON_PRIMARIO_SX}
                            >
                                {guardando ? "Guardando..." : "Guardar rango"}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={handleCancelar}
                                disabled={guardando}
                                fullWidth={isMobile}
                                sx={BOTON_SECUNDARIO_SX}
                            >
                                Cancelar
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Card>
        </Box>
    );
};

export default RangoForm;
